import { Signal } from 'typed-signals';
import PQueue from 'p-queue';
import { isPromise } from '~/lib/utils/func';
export enum PromisesQueueEventType {
  START = 'start',
  PROGRESS = 'progress',
  COMPLETE = 'complete',
  CANCEL = 'cancel',
  ERROR = 'error',
}

export default class PromisesQueueCalls {
  private _total: number;
  private _queue: { id: string; call: Function; priority: number }[];
  private _pQueue: PQueue;

  updateSignal = new Signal<
    (
      type: PromisesQueueEventType,
      value: { current: number; total: number; progress: number }
    ) => void
  >();

  constructor(concurrency: number = 10) {
    this._total = 0;
    this._queue = [];
    this._pQueue = new PQueue({ concurrency, autoStart: false });
    this._pQueue.on('next', () => {
      this.updateSignal.emit(PromisesQueueEventType.PROGRESS, this.state);
      if (this.progress === 1) {
        this.updateSignal.emit(PromisesQueueEventType.COMPLETE, this.state);
      }
    });
    this._pQueue.on('error', (e) => {
      this.updateSignal.emit(PromisesQueueEventType.ERROR, e);
    });
  }

  add(task: { id: string; call: Function; priority: number }) {
    this._queue.push(task);
  }

  get state() {
    return {
      progress: this.progress,
      current: this.current,
      total: this.total,
    };
  }

  get total() {
    return this._total;
  }

  get current() {
    return this._total - (this._pQueue.size + this._pQueue.pending);
  }

  get hasCalls() {
    return this._total > 0;
  }

  get progress() {
    if (this._total === 0) return 0;
    return this.current / this.total;
  }

  private async _processItem(call: Function): Promise<void> {
    const r = call();
    if (Array.isArray(r)) {
      await Promise.all(r);
    } else if (isPromise(r)) {
      await Promise.all([r]);
    } else {
      throw new Error('PromisesQueueCalls is not a promise');
    }
  }

  _converQueueToPQueue = () => {
    do {
      const task = this._queue.shift();
      this._pQueue.add(() => this._processItem(task.call), {
        id: task.id,
        priority: task.priority,
      });
    } while (this._queue.length > 0);
    this._total = this._pQueue.size;
  };

  cancel = () => {
    this._pQueue.pause();

    this.updateSignal.emit(PromisesQueueEventType.CANCEL, {
      progress: 0,
      current: 0,
      total: 1,
    });

    this._pQueue.clear();
  };

  execute = (concurrency: number) => {
    this._pQueue.pause();
    this._pQueue.clear();
    this._pQueue.concurrency = concurrency;
    this._converQueueToPQueue();

    this.updateSignal.emit(PromisesQueueEventType.START, {
      progress: 0,
      current: 0,
      total: this._total,
    });
    this._pQueue.start();
  };
}
